/*
 * Design reminder: keep the homepage behavior soft, lightweight, and faithful
 * to the editorial watercolor direction. JS only supports interaction, never layout hacks.
 */
(() => {
  const setLanguage = (lang) => {
    document.documentElement.dataset.lammaLang = lang;
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    document.querySelectorAll('[data-lamma-text]').forEach((wrapper) => {
      wrapper.querySelectorAll('[data-lang-value]').forEach((node) => {
        node.hidden = node.getAttribute('data-lang-value') !== lang;
      });
    });
    document.querySelectorAll('[data-lamma-toggle]').forEach((button) => {
      button.textContent = lang === 'ar' ? 'EN' : 'عربي';
    });
    localStorage.setItem('lammaLang', lang);
  };

  const initLanguage = () => {
    const stored = localStorage.getItem('lammaLang');
    const fallback = (document.documentElement.lang || '').toLowerCase().startsWith('en') ? 'en' : 'ar';
    setLanguage(stored || fallback);
    document.querySelectorAll('[data-lamma-toggle]').forEach((button) => {
      if (button.dataset.lammaBound === 'true') return;
      button.dataset.lammaBound = 'true';
      button.addEventListener('click', () => {
        const current = document.documentElement.dataset.lammaLang === 'en' ? 'en' : 'ar';
        setLanguage(current === 'ar' ? 'en' : 'ar');
      });
    });
  };

  const initHeader = () => {
    const header = document.querySelector('[data-lamma-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    document.querySelectorAll('[data-lamma-menu-toggle]').forEach((button) => {
      if (button.dataset.lammaBound === 'true') return;
      button.dataset.lammaBound = 'true';
      button.addEventListener('click', () => {
        const target = document.getElementById(button.getAttribute('aria-controls'));
        if (!target) return;
        const open = target.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    document.querySelectorAll('[data-lamma-menu] a').forEach((link) => {
      if (link.dataset.lammaBound === 'true') return;
      link.dataset.lammaBound = 'true';
      link.addEventListener('click', () => {
        const panel = link.closest('[data-lamma-menu]');
        if (panel) panel.classList.remove('is-open');
      });
    });
  };

  const initYouTube = () => {
    document.querySelectorAll('[data-lamma-youtube]').forEach((button) => {
      if (button.dataset.lammaBound === 'true') return;
      button.dataset.lammaBound = 'true';
      button.addEventListener('click', () => {
        const videoId = button.getAttribute('data-lamma-youtube');
        if (!videoId) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'lamma-video-frame';
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = button.getAttribute('data-video-title') || 'Lamma by Alia';
        wrapper.appendChild(iframe);
        button.replaceWith(wrapper);
      });
    });
  };

  const initTestimonials = () => {
    document.querySelectorAll('[data-lamma-carousel]').forEach((carousel) => {
      const cards = Array.from(carousel.querySelectorAll('[data-lamma-slide]'));
      const dots = Array.from(carousel.querySelectorAll('[data-lamma-dot]'));
      if (!cards.length) return;
      let active = Math.max(0, cards.findIndex((card) => card.classList.contains('is-active')));
      if (active < 0) active = 0;

      const render = () => {
        cards.forEach((card, index) => card.classList.toggle('is-active', index === active));
        dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
      };

      carousel.querySelectorAll('[data-lamma-prev]').forEach((button) => {
        if (button.dataset.lammaBound === 'true') return;
        button.dataset.lammaBound = 'true';
        button.addEventListener('click', () => {
          const lang = document.documentElement.dataset.lammaLang === 'en' ? 'en' : 'ar';
          active = lang === 'ar' ? (active + 1) % cards.length : (active - 1 + cards.length) % cards.length;
          render();
        });
      });

      carousel.querySelectorAll('[data-lamma-next]').forEach((button) => {
        if (button.dataset.lammaBound === 'true') return;
        button.dataset.lammaBound = 'true';
        button.addEventListener('click', () => {
          const lang = document.documentElement.dataset.lammaLang === 'en' ? 'en' : 'ar';
          active = lang === 'ar' ? (active - 1 + cards.length) % cards.length : (active + 1) % cards.length;
          render();
        });
      });

      dots.forEach((dot, index) => {
        if (dot.dataset.lammaBound === 'true') return;
        dot.dataset.lammaBound = 'true';
        dot.addEventListener('click', () => {
          active = index;
          render();
        });
      });

      render();
    });
  };

  const initWhatsApp = () => {
    document.querySelectorAll('[data-lamma-wa]').forEach((widget) => {
      const toggle = widget.querySelector('[data-lamma-wa-toggle]');
      const close = widget.querySelector('[data-lamma-wa-close]');
      if (toggle && toggle.dataset.lammaBound !== 'true') {
        toggle.dataset.lammaBound = 'true';
        toggle.addEventListener('click', () => widget.classList.toggle('is-open'));
      }
      if (close && close.dataset.lammaBound !== 'true') {
        close.dataset.lammaBound = 'true';
        close.addEventListener('click', () => widget.classList.remove('is-open'));
      }
    });
  };

  const initTikTok = () => {
    if (!document.querySelector('.tiktok-embed')) return;
    if (!document.getElementById('lamma-tiktok-embed-script')) {
      const script = document.createElement('script');
      script.id = 'lamma-tiktok-embed-script';
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    } else if (window.tiktokEmbed && window.tiktokEmbed.lib && typeof window.tiktokEmbed.lib.render === 'function') {
      window.tiktokEmbed.lib.render();
    }
  };

  const init = () => {
    if (!document.body.classList.contains('template-index')) return;
    initLanguage();
    initHeader();
    initYouTube();
    initTestimonials();
    initWhatsApp();
    initTikTok();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
